import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import client from "@/lib/api";
import {
  Play, Star, Sparkles, CheckCircle2, Film, BookOpen, HelpCircle,
  Users, ShieldCheck, Heart, Ban, MoonStar, School, BadgeCheck, Gift, Youtube, Quote
} from "lucide-react";
import PersonaFork from "@/components/PersonaFork";
import StoryHeroCarousel from "@/components/StoryHeroCarousel";
import FreeStoryShowcase from "@/components/FreeStoryShowcase";
import { SHOW_PAYMENT } from "@/lib/paymentVisibility";
import AskMySchool from "@/components/AskMySchool";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// Placeholder YouTube video — replace with the real HikmahVerses sample once available.
// Just swap SAMPLE_YOUTUBE_ID to the new 11-char YouTube ID (e.g. "abc123XYZ_9").
// Leave empty ("") to show the "sample coming soon" state.
const SAMPLE_YOUTUBE_ID = "";

// Warm, modest, family-safe imagery — no Prophet depictions
const IMG = {
  // Real Mushaf page — for the Monday "New Story" card
  ep1: "/landing/story-video.jpeg",
  ep2: "/landing/quiz.jpeg",
  // Replaced 2026-09-03: the two URLs previously here were astrophotography
  // (a night sky and a galaxy) — visually confirmed and swapped for on-theme
  // library/reading photos after they showed up wrong on the weekly cards.
  ep3: "/landing/morals.jpeg",
  ep4: "https://images.unsplash.com/photo-1506880018603-83d5b814b5a6?w=600&auto=format&fit=crop&q=70",
  // Pen + handwritten notebook against a bookshelf — for the Saturday quiz card
  // (visually confirmed 2026-09-03, see /scratchpad/candidate1.jpg check)
  ep5: "https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=600&auto=format&fit=crop&q=70",
  storybook: "/landing/book.jpeg",
  // Two more distinct, visually-confirmed library photos (2026-09-04) so
  // the Growing Library mosaic below stops repeating ep1/ep2 a second
  // time across its 7 slots.
  libraryHall: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=600&auto=format&fit=crop&q=70",
  libraryShelf: "https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=600&auto=format&fit=crop&q=70",
  // Illustrated teacher with a children's storybook library — for weekly live class
  live: "https://customer-assets-jt897jd0.emergentagent.net/job_ayah-daily-8/artifacts/zesqg5b4_image.png",
  // Illustrated Muslim family on sofa — for "Why parents choose it"
  family: "https://customer-assets-jt897jd0.emergentagent.net/job_ayah-daily-8/artifacts/8csjy3ef_image.png",
};

// Real 3D-animated story covers, in actual story order (global_sequence 1-4
// confirmed against SAMPLE_STORIES in server.py: Healing Power of Surah
// Al-Fatihah, The Angels Descended, The Seal on Abu Jahl's Heart, The
// Magnificence of the Qur'an — stories 5-10 follow in the same sequence).
// Replaces the old generic Unsplash stock photos in the Growing Library
// shelf with the site's own branded "600 Stories Series" cover art.
// .webp, not the original WhatsApp .jpg exports (~200-500KB each) — cut to
// ~45-80KB apiece (resized to 700px, q78) so this section doesn't stall the
// landing page's first load.
const STORY_COVERS = [
  "/covers/story-1.webp", "/covers/story-2.webp", "/covers/story-3.webp", "/covers/story-4.webp",
  "/covers/story-5.webp", "/covers/story-6.webp", "/covers/story-7.webp", "/covers/story-8.webp",
  "/covers/story-9.webp", "/covers/story-10.webp",
];

// Art-only crops of the same 10 covers — each one's baked-in title/byline
// band cropped off (boundary detected per-image via a brightness-plateau
// scan, then hand-verified against every image; the band's height isn't
// uniform since it depends on how many lines the cover's own title wraps
// to). Used for the small library-grid thumbnails, where the card already
// renders its own "#N · Juz X" + title text right next to the image, so
// the cover's own baked-in title would just be confusing duplicate/
// conflicting text at thumbnail size. STORY_COVERS (full cover, title
// included) stays reserved for the hero carousel, which is meant to look
// like a real book cover.
const STORY_COVERS_ART = [
  "/covers/story-1-art.webp", "/covers/story-2-art.webp", "/covers/story-3-art.webp", "/covers/story-4-art.webp",
  "/covers/story-5-art.webp", "/covers/story-6-art.webp", "/covers/story-7-art.webp", "/covers/story-8-art.webp",
  "/covers/story-9-art.webp", "/covers/story-10-art.webp",
];

// Same 10 covers, paired with their real titles (read directly off each
// cover's own baked-in artwork) — the data StoryHeroCarousel renders in the
// hero. Titles aren't shown again on top of the cards since the cover art
// already carries its own title treatment; they're only used for alt text
// and the dot-indicator/aria labels.
// Verified 2026-09-30 against the real GET /stories/{1..10} titles — the
// previous #5-9 entries here were unverified placeholders (never actually
// rendered on screen, per the comment above) and had drifted from the
// real content.
const STORY_TITLES = [
  "The Healing Power of Surah Al-Fatihah",
  "The Angels Descended",
  "The Seal on Abu Jahl's Heart",
  "The Magnificence of the Qur'an",
  "A Small Example, A Big Lesson",
  "The Creation of Adam (عَلَيْهِ ٱلسَّلَام)",
  "Adam: From Paradise to Earth",
  "Bani Israel's Rescue from Pharaoh's Tyranny",
  "Food and Water for Bani Israel",
  "Disobedient People Turned into Pigs and Monkeys",
];

const STORY_SHOWCASE = STORY_COVERS.map((image, i) => ({
  image,
  artImage: STORY_COVERS_ART[i],
  title: STORY_TITLES[i],
}));

// Rotates through the app's existing pastel tile tokens (index.css) —
// same mint/pink/cream/lavender language the rest of the site already
// uses for stat tiles, applied here as each story card's background.
const STORY_CARD_TONES = ["hv-tile-teal", "hv-tile-rose", "hv-tile-gold", "hv-tile-lavender"];

const Chip = ({ children, testid }) => (
  <span data-testid={testid} className="inline-flex items-center gap-2 rounded-full bg-white border border-[#E5DFC8] px-3.5 py-1.5 text-xs font-bold text-hv-navy shadow-sm">
    <CheckCircle2 size={13} className="text-hv-teal" strokeWidth={3} /> {children}
  </span>
);

function WatchSampleDialog({ triggerClassName = "hv-btn-secondary", testid = "cta-watch-sample" }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={testid} className={`${triggerClassName} inline-flex items-center gap-2`}>
          <Play size={16} className="text-hv-navy" fill="#15274D" strokeWidth={0} /> Watch Sample
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl p-0 bg-hv-navy border-none overflow-hidden" data-testid="watch-sample-modal">
        <DialogTitle className="sr-only">Sample story preview</DialogTitle>
        <DialogDescription className="sr-only">A short preview of a HikmahVerses 600 Stories episode.</DialogDescription>
        <div className="aspect-video w-full">
          {open && SAMPLE_YOUTUBE_ID ? (
            <iframe
              data-testid="sample-youtube-iframe"
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${SAMPLE_YOUTUBE_ID}?autoplay=1&rel=0&modestbranding=1`}
              title="HikmahVerses sample story"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-center px-8 gap-4 relative" data-testid="sample-coming-soon">
              <div className="absolute inset-0 opacity-10">
                <div className="w-full h-full" style={{
                  backgroundImage: "radial-gradient(circle at 20% 20%, #E2C27E 0, transparent 40%), radial-gradient(circle at 80% 60%, #5E9A93 0, transparent 40%)"
                }} />
              </div>
              <div className="hv-icon-circle-coral w-16 h-16 rounded-full flex items-center justify-center relative">
                <Play size={26} fill="white" strokeWidth={0} />
              </div>
              <div className="hv-heading text-white text-2xl relative">Sample video coming soon</div>
              <p className="text-white/70 max-w-md relative">
                We&apos;re polishing our first Qur&apos;anic story preview. Create a free account to start watching the stories already released.
              </p>
            </div>
          )}
        </div>
        <div className="p-4 flex items-center justify-between gap-4 bg-hv-navy">
          <div className="text-white">
            <div className="text-[10px] uppercase tracking-[0.2em] text-hv-gold font-bold">Sample story</div>
            <div className="hv-heading text-white text-base">A 5-minute Qur&apos;anic episode</div>
          </div>
          <Link to="/signup" data-testid="modal-signup-cta" className="hv-btn-primary py-2.5 px-5 text-sm inline-flex items-center gap-2">
            {SHOW_PAYMENT ? "Start Membership · $10/mo" : "Create free account"}
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Landing() {
  const [recap, setRecap] = useState({ youtube_url: null, schedule_note: null });
  useEffect(() => {
    client.get("/weekly-recap/latest").then(({ data }) => setRecap(data)).catch(() => {});
  }, []);
  const { t } = useLang();
  const location = useLocation();

  // "/" and "/families" render this same page on purpose (families are the
  // main-traffic audience) — but that means the "For Families" nav link and
  // the PersonaFork card land on a page that looks byte-identical to the one
  // the visitor is already on, which reads as a dead/broken click. Landing on
  // /families specifically jumps straight to pricing, so the click visibly
  // does something and a parent lands on their actual next step.
  useEffect(() => {
    if (location.pathname !== "/families") return;
    const el = document.getElementById("pricing");
    if (!el) return;
    const id = requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
    return () => cancelAnimationFrame(id);
  }, [location.pathname]);

  // The full Monday-Sunday routine: one story a week (paid members can add a second), each followed by its
  // storybook and a quiz, then a recap video on Sunday. Every card carries a
  // real image plus a badge naming what it is — no blank placeholder slots.
  const weekly = [
    { day: "Mon", title: "Story Video", sub: "This week's animated story — the first watch of the week.",
      img: IMG.ep1, badgeIcon: <Film size={12} strokeWidth={3} />, badgeLabel: "New story" },
    { day: "Tue", title: "Storybook", sub: "The same story as a page-flip storybook, read together.",
      img: IMG.storybook, badgeIcon: <BookOpen size={12} strokeWidth={3} />, badgeLabel: "Storybook" },
    { day: "Wed", title: "Morals", sub: "Three simple morals, and the Ayah the story connects to.",
      img: IMG.ep3, pos: "top center", zoom: 1.12, badgeIcon: <Sparkles size={12} strokeWidth={3} />, badgeLabel: "Morals" },
    { day: "Thu", title: "Quiz", sub: "A short, gentle quiz on the story that earns stars.",
      img: IMG.ep2, badgeIcon: <HelpCircle size={12} strokeWidth={3} />, badgeLabel: "Quiz" },
    { day: "Fri", title: "Reflection", sub: "Write what your child learned, in their own words.",
      img: IMG.ep4, badgeIcon: <Sparkles size={12} strokeWidth={3} />, badgeLabel: "Write" },
    { day: "Sat", title: "Review", sub: "Go back over the morals together with a gentle recap.",
      img: IMG.ep5, badgeIcon: <BookOpen size={12} strokeWidth={3} />, badgeLabel: "Review" },
    { day: "Sun", title: "Live recap", sub: "A recorded video with your child's teacher, recapping the week — posted on YouTube.",
      img: IMG.live, badgeIcon: <Youtube size={12} strokeWidth={3} />, badgeLabel: "YouTube", live: true },
  ];

  const steps = [
    { n: 1, title: "Watch the story", sub: "A bright animated episode — safe visuals, vocals-only audio.", icon: <Film size={22} strokeWidth={2.5} />, tone: "teal" },
    { n: 2, title: "Read the storybook", sub: "The same story as a hand-illustrated flip book.", icon: <BookOpen size={22} strokeWidth={2.5} />, tone: "coral" },
    { n: 3, title: "Ayah of the Day", sub: "The Qur'an verse this story connects to — explained simply.", icon: <Quote size={22} strokeWidth={2.5} />, tone: "gold" },
    { n: 4, title: "Remember 3 morals", sub: "Three simple lessons drawn from the story.", icon: <Heart size={22} strokeWidth={2.5} />, tone: "teal" },
    { n: 5, title: "Take the quiz", sub: "A few fun questions to lock in what they just learned.", icon: <HelpCircle size={22} strokeWidth={2.5} />, tone: "coral" },
  ];
  const stepToneClass = { teal: "hv-icon-circle", coral: "hv-icon-circle-coral", gold: "hv-icon-circle-gold" };

  const whyBullets = [
    { title: "Nothing random, ever", sub: "One handpicked episode a week — no autoplay, no algorithm choosing what's next." },
    { title: "More than a cartoon", sub: "Every story adds 3 morals, a Word of the Day, and a quiz." },
    { title: "Easy for busy parents", sub: "The whole week is planned for you — you just press play." },
    { title: "Progress you can see", sub: "Quiz scores and streaks parents can check anytime — not just at report card time." },
    { title: "Built for every child", sub: "Link each sibling's own account and watch them each grow at their own pace." },
    { title: "Backed by a guarantee", sub: "Juz certificates to show progress, full refund in week 1." },
  ];

  const trust = [
    { icon: <Ban size={18} strokeWidth={2.5} />, label: "No depictions of Prophets" },
    { icon: <ShieldCheck size={18} strokeWidth={2.5} />, label: "Modest, careful visuals" },
    { icon: <CheckCircle2 size={18} strokeWidth={2.5} />, label: "Religious QA before release" },
    { icon: <Heart size={18} strokeWidth={2.5} />, label: "Age-appropriate morals" },
    { icon: <MoonStar size={18} strokeWidth={2.5} />, label: "Vocals-only, no instruments" },
  ];

  // Detailed feature list — appears on every plan card
  const includedFeatures = [
    "1 new Qur'anic story every week",
    "Illustrated storybook for every story",
    "Interactive quiz + Word of the Day",
    "3 morals drawn from every story",
    "One weekly live class with a teacher",
    "Full growing library — always yours to re-watch",
    "Family Mode for bedtime reading",
    "English at launch — more languages coming",
    "Religious QA review on every episode",
    "Certificate on every Juz completed",
  ];

  // Matches the backend's real free-plan rule: the first 4 stories (one
  // month at the default pace), then a paid plan is needed to go further.
  const freeFeatures = [
    "Your first 4 Qur'anic stories",
    "Storybook + interactive quiz",
    "Word of the Day",
    "Community feed access",
    "English at launch — more languages coming",
  ];
  const freeExcluded = [
    "Story 5 onward needs a paid plan",
    "No weekly live class",
    "No Family Mode",
    "No completion certificates",
  ];

  const familyTiers = [
    { kid: "1st child", price: 100, discount: 0 },
    { kid: "2nd child", price: 75, discount: 25 },
    { kid: "3rd child", price: 50, discount: 50 },
    { kid: "4th child", price: 25, discount: 75 },
    { kid: "5th child", price: 0, discount: 100 },
  ];

  const faqs = [
    { q: "Is there a free plan?", a: "Yes — your first 4 stories are free for one month, no card required, with their storybooks, quizzes, Word of the Day, and community access. To continue into month 2 and beyond, you'll need a paid plan, which also unlocks the full 600-story journey, weekly live classes, Family Mode, and completion certificates." },
    { q: "How much does it cost?", a: "$10/month, or $100/year (that's 2 months free). Family bundles start at $100 for the first child with 25% / 50% / 75% / 100% off each additional child up to 5. Schools and madrasas get custom bulk pricing." },
    { q: "Can I get my money back?", a: "Yes. Try any plan for a full week — if it's not right for your family, cancel in week 1 and we'll refund your subscription in full. No questions asked." },
    { q: "Do you have a plan for schools or madrasas?", a: "Yes. Institutions get tiered pricing (10–49 / 50–199 / 200+ students), a teacher dashboard, cohort-wide progress tracking, and bulk enrollment. Email operations@hikmahverses.com to talk." },
    { q: "Is this just cartoons?", a: "No. Each story comes with an illustrated storybook, a quiz, a Word of the Day, three simple morals, and a weekly live class with a teacher." },
    { q: "What ages is this for?", a: "Designed for ages 8 to 13 (grades 4 to 8). A separate Family Mode makes it work as a bedtime routine for younger siblings too." },
    { q: "How many stories each week?", a: "One story each week, released on Monday, with its storybook, quiz, reflection and a Sunday live recap." },
    { q: "Do we get old stories too?", a: "Yes. Every unlocked story stays in your library forever — catch up any time, no pressure." },
    { q: "Is the content Islamically careful?", a: "Yes. No depictions of Prophets, modest visuals, vocals-only audio, and religious QA review before any story is released." },
  ];
  const PAYMENT_FAQ_QUESTIONS = ["Is there a free plan?", "How much does it cost?", "Can I get my money back?", "Do you have a plan for schools or madrasas?"];
  const visibleFaqs = SHOW_PAYMENT ? faqs : faqs.filter((f) => !PAYMENT_FAQ_QUESTIONS.includes(f.q));

  return (
    <main className="pb-24" data-testid="landing-page">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-6">
      </div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 pb-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <span className="hv-badge" data-testid="hero-badge">
              <Star size={12} className="text-hv-gold" fill="#E2C27E" /> For Muslim Families
            </span>
            <h1 className="hv-heading mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              <span className="text-hv-navy">Qur&apos;anic cartoons your kids can</span>{" "}
              <span className="text-hv-coral">enjoy</span>{" "}
              <span className="text-hv-navy">— and you can</span>{" "}
              <span className="text-hv-teal">trust</span>
              <span className="text-hv-navy">.</span>
            </h1>
            <p className="mt-6 text-hv-slate text-lg max-w-xl leading-relaxed">
              A new Qur&apos;anic story every week, with storybooks, quizzes, a growing library, and a weekly live class{SHOW_PAYMENT && <> — for <span className="font-bold text-hv-navy">$10 / month</span></>}. Paid members can add a second story each week.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {SHOW_PAYMENT ? (
                <Link to="/signup" data-testid="cta-start-membership" className="hv-btn-primary inline-flex items-center gap-2">
                  <Play size={18} fill="white" strokeWidth={0} /> Start Membership <span className="opacity-80">· $10/mo</span>
                </Link>
              ) : (
                <Link to="/signup" data-testid="cta-start-membership" className="hv-btn-primary inline-flex items-center gap-2">
                  <Play size={18} fill="white" strokeWidth={0} /> Create free account
                </Link>
              )}
              <WatchSampleDialog />
            </div>
            <div className="mt-6 flex flex-wrap gap-2" data-testid="hero-chips">
              <Chip testid="chip-1-story">1 story weekly</Chip>
              <Chip testid="chip-book-quiz">Storybooks + quizzes</Chip>
              <Chip testid="chip-live">Weekly recap video</Chip>
              <Chip testid="chip-islamic-care">Islamic care process</Chip>
              <Chip testid="chip-cancel">Cancel anytime</Chip>
            </div>
          </div>

          <motion.div
            className="lg:col-span-6 relative"
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <div className="relative max-w-sm mx-auto lg:max-w-md">
              <StoryHeroCarousel stories={STORY_SHOWCASE} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* A LITTLE PEEK — mirrors the sibling Little Verses app's own
          autoplay free-preview section: no auth check at all, the video
          just plays. "Open the full story" navigates to the real player
          (/story/1); gating from there happens inside StoryDetail itself
          based on auth state — see FREE_PREVIEW_SEQ on the backend and the
          tabLocked logic in StoryDetail.js. Sits between the hero and the
          families/schools fork so either audience sees it before having to
          pick a path. */}
      <FreeStoryShowcase fallbackTitle={STORY_SHOWCASE[0].title} />

      {/* WHO IS THIS FOR — families vs schools fork */}
      <PersonaFork />

      {/* THIS WEEK INSIDE */}
      <section id="weekly" className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <div className="max-w-2xl">
          <span className="hv-badge">This week inside</span>
          <h2 className="hv-heading mt-4 text-3xl sm:text-4xl">
            <span className="text-hv-navy">What your child</span>{" "}
            <span className="text-hv-coral">gets this week.</span>
          </h2>
          <p className="mt-3 text-hv-slate leading-relaxed">Monday to Sunday, mapped out for you — one story with its storybook, quiz, and reflection, then a Sunday recap. Paid members can switch on a second story each week. Miss a day? Nothing locks — you can always catch up.</p>
        </div>

        {/* One row from lg (1024px) up — the xl (1280px) threshold used to wrap
            to two rows on an ordinary laptop width. Text is never truncated;
            cards simply grow as tall as their own content needs. */}
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4" data-testid="weekly-grid">
          {weekly.map((w, i) => (
            <div key={i} className="hv-card overflow-hidden flex flex-col" data-testid={`week-card-${w.day}`}>
              <div className="aspect-square bg-hv-navy relative overflow-hidden">
                <img src={w.img} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: w.pos || "center", transform: w.zoom ? `scale(${w.zoom})` : undefined, transformOrigin: "top center" }} />
                <span className={`absolute top-1.5 start-1.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] sm:text-[10px] font-bold ${w.live ? "bg-hv-coral text-white" : "bg-white text-hv-navy"}`}>
                  {w.live && <span className="w-1 h-1 rounded-full bg-white animate-pulse" />}
                  <span aria-label={w.badgeLabel}>{w.badgeIcon}</span>
                </span>
              </div>
              <div className="p-2.5 sm:p-3.5">
                <div className="text-[9px] sm:text-[10px] uppercase tracking-wide text-hv-teal font-bold">{w.day}</div>
                <div className="hv-heading text-hv-navy text-sm sm:text-base mt-0.5 leading-tight">{w.title}</div>
                <div className="mt-1.5 text-hv-slate text-xs sm:text-sm leading-snug">{w.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* EVERY STORY, A LEARNING PACK */}
      <section id="how-it-works" className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <div className="max-w-2xl">
          <span className="hv-badge">Every story, a learning pack</span>
          <h2 className="hv-heading mt-4 text-3xl sm:text-4xl">
            <span className="text-hv-navy">Watch. Read. Quiz.</span>{" "}
            <span className="text-hv-coral">Remember.</span>
          </h2>
          <p className="mt-3 text-hv-slate leading-relaxed">Each story turns into 5 small steps your child enjoys.</p>
        </div>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-5 gap-6">
          {steps.map((s) => (
            <div
              key={s.n}
              className="relative hv-card p-6 pt-10 transition-all duration-200 hover:-translate-y-1.5 hover:shadow-xl"
              data-testid={`learning-step-${s.n}`}
            >
              <div className="hv-num-badge">{s.n}</div>
              <div className={stepToneClass[s.tone]}>{s.icon}</div>
              <div className="mt-4 hv-heading text-hv-navy text-lg">{s.title}</div>
              <div className="mt-2 text-hv-slate text-sm leading-relaxed">{s.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY PARENTS CHOOSE IT */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="hv-card overflow-hidden">
              <img src={IMG.family} alt="A modest Muslim family enjoying stories together" className="w-full aspect-[5/4] object-cover" />
            </div>
          </div>
          <div className="lg:col-span-6">
            <span className="hv-badge">Why parents choose it</span>
            <h2 className="hv-heading mt-4 text-3xl sm:text-4xl">
              <span className="text-hv-navy">Screen time that feels</span>{" "}
              <span className="text-hv-coral">meaningful.</span>
            </h2>
            <p className="mt-3 text-hv-slate leading-relaxed">
              You already let them watch. This makes that time count — a real routine, a real teacher, gently Islamic, every week.
            </p>
            <ul className="mt-6 space-y-4">
              {whyBullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`why-bullet-${i}`}>
                  <div className="hv-icon-circle-gold w-9 h-9 rounded-full flex items-center justify-center shrink-0">
                    <CheckCircle2 size={18} strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="hv-heading text-hv-navy text-lg">{b.title}</div>
                    <div className="text-hv-slate text-sm">{b.sub}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* GROWING LIBRARY */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div className="max-w-2xl">
            <span className="hv-badge">Growing library</span>
            <h2 className="hv-heading mt-4 text-3xl sm:text-4xl">
              <span className="text-hv-navy">A new Islamic story library</span>{" "}
              <span className="text-hv-coral">growing every week.</span>
            </h2>
            <p className="mt-3 text-hv-slate leading-relaxed">
              One story weekly · 600 stories · always yours to re-watch.
            </p>
          </div>
          <span className="hv-badge hv-badge-coral" data-testid="new-this-week-badge">+1 new this week</span>
        </div>

        <div className="mt-10 grid sm:grid-cols-2 gap-5" data-testid="library-shelf">
          {STORY_SHOWCASE.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.4, ease: "easeOut", delay: (i % 2) * 0.08 }}
            >
              <Link
                to={`/story/${i + 1}`}
                data-testid={`shelf-ep-${i + 1}`}
                className={`relative flex items-center gap-4 rounded-[1.75rem] p-3 pe-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all ${STORY_CARD_TONES[i % STORY_CARD_TONES.length]}`}
              >
                <div className="w-[160px] h-[125px] sm:w-[200px] sm:h-[155px] rounded-2xl overflow-hidden shrink-0 bg-hv-navy">
                  <img src={s.artImage} alt={s.title} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-bold text-hv-teal">#{i + 1} · Juz 1</div>
                  <div className="hv-heading text-hv-navy text-base sm:text-lg leading-snug mt-1 line-clamp-2">
                    {s.title}
                  </div>
                </div>
                <span className="absolute top-3 end-3 w-7 h-7 rounded-full bg-white shadow flex items-center justify-center text-xs font-bold text-hv-navy shrink-0">
                  {i + 1}
                </span>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Stats */}
        <div className="mt-10 grid grid-cols-3 gap-4 max-w-4xl">
          {[
            { n: "1", l: "new story / week" },
            { n: "600", l: "stories over 60 months" },
            { n: "1", l: "weekly live class" },
          ].map((s, i) => (
            <div key={i} className="hv-card p-6" data-testid={`stat-${i}`}>
              <div className="text-4xl sm:text-5xl font-heading font-bold text-hv-teal leading-none">{s.n}</div>
              <div className="mt-2 text-xs sm:text-sm text-hv-slate uppercase tracking-wider font-bold">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* LIVE CLASS */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="hv-card overflow-hidden relative">
              {recap.youtube_url ? (
                <div className="aspect-[4/3] bg-hv-navy">
                  <iframe src={recap.youtube_url} title="Weekly recap video" className="w-full h-full" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-testid="landing-recap-iframe" />
                </div>
              ) : (
                <img src={IMG.live} alt="Weekly recap video" className="w-full aspect-[4/3] object-cover" />
              )}
              <div className="absolute top-4 start-4 hv-badge hv-badge-coral" data-testid="live-badge">
                <Youtube size={12} strokeWidth={3} /> {recap.schedule_note || "Sunday · YouTube"}
              </div>
              <div className="absolute bottom-4 end-4 hv-card px-3 py-2 flex items-center gap-2">
                <Users size={16} className="text-hv-teal" strokeWidth={2.5} />
                <div className="leading-tight">
                  <div className="hv-heading text-hv-navy text-sm">120+ kids</div>
                  <div className="text-[10px] text-hv-slate">watched last week</div>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6">
            <span className="hv-badge">Weekly recap videos</span>
            <h2 className="hv-heading mt-4 text-3xl sm:text-4xl">
              <span className="text-hv-navy">Guided learning</span>{" "}
              <span className="text-hv-coral">every week.</span>
            </h2>
            <p className="mt-3 text-hv-slate leading-relaxed">
              A teacher records a recap of the stories, explains the Word of the Day, and helps children remember the morals — posted every Sunday.
            </p>
            <ul className="mt-6 space-y-3">
              {["A real teacher, on screen", "Word of the Day explained simply", "3 morals reviewed together", "Watch any time it suits your evening"].map((f, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`live-bullet-${i}`}>
                  <div className="hv-icon-circle w-8 h-8 rounded-full flex items-center justify-center shrink-0">
                    <CheckCircle2 size={16} strokeWidth={3} />
                  </div>
                  <div className="hv-heading text-hv-navy text-base">{f}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* MADE WITH ISLAMIC CARE */}
      <section id="promise" className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <div className="max-w-2xl">
          <span className="hv-badge">Parent trust</span>
          <h2 className="hv-heading mt-4 text-3xl sm:text-4xl">
            <span className="text-hv-navy">Made with</span>{" "}
            <span className="text-hv-coral">Islamic care.</span>
          </h2>
          <p className="mt-3 text-hv-slate leading-relaxed">Built with the boundaries Muslim parents quietly look for.</p>
        </div>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {trust.map((t, i) => (
            <div key={i} className="hv-card p-5 flex items-center gap-3" data-testid={`trust-${i}`}>
              <div className="hv-icon-circle w-10 h-10 rounded-full flex items-center justify-center shrink-0">
                {t.icon}
              </div>
              <div className="hv-heading text-hv-navy text-sm leading-snug">{t.label}</div>
            </div>
          ))}
        </div>
        <div className="mt-6 hv-card p-5 flex items-center gap-3 max-w-3xl" data-testid="trust-note">
          <ShieldCheck size={22} className="text-hv-teal shrink-0" strokeWidth={2.5} />
          <div className="text-hv-slate text-sm">
            <span className="font-bold text-hv-navy">Built for Muslim families.</span> A religious QA process is applied to every story before release.
          </div>
        </div>
      </section>

      {SHOW_PAYMENT && (
      <section id="pricing" className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <div className="max-w-2xl mx-auto text-center">
          <span className="hv-badge">Pricing</span>
          <h2 className="hv-heading mt-4 text-3xl sm:text-4xl">
            <span className="text-hv-navy">Simple plans.</span>{" "}
            <span className="text-hv-coral">Serious value.</span>
          </h2>
          <p className="mt-3 text-hv-slate leading-relaxed">
            Pick monthly, save with yearly, or bundle the whole family. Every plan includes everything below.
          </p>
        </div>

        {/* Four plans */}
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch" data-testid="pricing-grid">
          {/* FREE */}
          <div className="hv-card p-7 flex flex-col" data-testid="plan-free">
            <span className="hv-badge self-start">First Month Free</span>
            <h3 className="hv-heading mt-4 text-xl text-hv-navy">Free first month</h3>
            <div className="mt-4 flex items-baseline gap-2">
              <div className="hv-heading text-5xl text-hv-navy">$0</div>
              <div className="text-hv-slate">/ first month</div>
            </div>
            <p className="mt-2 text-hv-slate text-sm">Your first 4 stories, on us — no card required.</p>
            <Link to="/signup?plan=free" data-testid="plan-free-cta" className="hv-btn-secondary mt-6 inline-flex items-center justify-center gap-2">
              <Gift size={16} strokeWidth={2.5} /> Start Free
            </Link>
            <ul className="mt-6 space-y-2 border-t border-[#E5DFC8] pt-5">
              {freeFeatures.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-hv-navy" data-testid={`free-feature-${i}`}>
                  <CheckCircle2 size={16} className="text-hv-teal mt-0.5 shrink-0" strokeWidth={2.5} />
                  <span>{f}</span>
                </li>
              ))}
              {freeExcluded.map((f, i) => (
                <li key={`x-${i}`} className="flex items-start gap-2 text-sm text-hv-slate" data-testid={`free-excluded-${i}`}>
                  <Ban size={16} className="text-hv-slate/60 mt-0.5 shrink-0" strokeWidth={2.5} />
                  <span className="line-through">{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* MONTHLY */}
          <div className="hv-card p-7 flex flex-col" data-testid="plan-monthly">
            <span className="hv-badge self-start">Monthly</span>
            <h3 className="hv-heading mt-4 text-xl text-hv-navy">Try it month by month</h3>
            <div className="mt-4 flex items-baseline gap-2">
              <div className="hv-heading text-5xl text-hv-navy">$10</div>
              <div className="text-hv-slate">/ month</div>
            </div>
            <p className="mt-2 text-hv-slate text-sm">Cancel anytime. No contracts.</p>
            <Link to="/signup?plan=monthly" data-testid="plan-monthly-cta" className="hv-btn-secondary mt-6 inline-flex items-center justify-center gap-2">
              Start Monthly
            </Link>
            <ul className="mt-6 space-y-2 border-t border-[#E5DFC8] pt-5">
              {includedFeatures.slice(0, 6).map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-hv-navy">
                  <CheckCircle2 size={16} className="text-hv-teal mt-0.5 shrink-0" strokeWidth={2.5} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* YEARLY — RECOMMENDED */}
          <div className="hv-card p-7 flex flex-col bg-hv-navy text-white relative overflow-hidden ring-4 ring-hv-coral/40" data-testid="plan-yearly">
            <span className="absolute top-4 end-4 hv-badge hv-badge-coral" data-testid="best-value-badge">Best value</span>
            <span className="hv-badge hv-badge-coral self-start">Yearly</span>
            <h3 className="hv-heading mt-4 text-xl text-white">Best for one child</h3>
            <div className="mt-4 flex items-baseline gap-2">
              <div className="hv-heading text-5xl text-hv-gold">$100</div>
              <div className="text-white/70">/ year</div>
            </div>
            <div className="mt-2 flex items-center gap-2 text-sm flex-wrap">
              <span className="line-through text-white/50">$120</span>
              <span className="hv-badge hv-badge-coral" data-testid="yearly-savings">Save $20 · 2 months free</span>
            </div>
            <Link to="/signup?plan=yearly" data-testid="plan-yearly-cta" className="hv-btn-primary mt-6 inline-flex items-center justify-center gap-2">
              <Play size={16} fill="white" strokeWidth={0} /> Start Yearly
            </Link>
            <ul className="mt-6 space-y-2 border-t border-white/15 pt-5">
              {includedFeatures.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-white">
                  <CheckCircle2 size={16} className="text-hv-gold mt-0.5 shrink-0" strokeWidth={2.5} />
                  <span style={{ color: "#FFFFFF" }}>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* FAMILY BUNDLE */}
          <div className="hv-card p-7 flex flex-col" data-testid="plan-family">
            <span className="hv-badge self-start">Family Bundle · Yearly</span>
            <h3 className="hv-heading mt-4 text-xl text-hv-navy">The more kids, the more you save</h3>
            <p className="mt-2 text-hv-slate text-sm">
              The 5th child is <span className="font-bold text-hv-coral">completely free</span>.
            </p>

            <div className="mt-4 rounded-2xl bg-hv-cream p-3.5 border border-[#E5DFC8]" data-testid="family-tier-list">
              {familyTiers.map((f, i) => (
                <div key={i} className={`flex items-center justify-between py-1.5 ${i > 0 ? "border-t border-[#E5DFC8]" : ""}`} data-testid={`family-tier-${i + 1}`}>
                  <div className="flex items-center gap-2">
                    <div className="hv-icon-circle w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold">{i + 1}</div>
                    <div className="text-xs font-bold text-hv-navy">{f.kid}</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {f.discount > 0 && (
                      <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-hv-coral">
                        {f.discount === 100 ? "FREE" : `-${f.discount}%`}
                      </span>
                    )}
                    <div className="hv-heading text-hv-navy text-base">${f.price}</div>
                  </div>
                </div>
              ))}
              <div className="mt-2 pt-2 border-t border-[#E5DFC8] flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-hv-teal">5 kids total</div>
                <div className="hv-heading text-hv-navy text-base">
                  $250<span className="text-hv-slate text-xs font-normal"> / yr</span>
                </div>
              </div>
              <div className="mt-1 text-[11px] text-hv-slate text-end">
                <span className="font-bold text-hv-coral">$50 / child / year</span>
              </div>
            </div>

            <Link to="/signup?plan=family" data-testid="plan-family-cta" className="hv-btn-primary mt-6 inline-flex items-center justify-center gap-2">
              <Gift size={16} strokeWidth={2.5} /> Start Family Bundle
            </Link>
            <div className="mt-3 text-hv-slate text-xs text-center">Up to 5 children per household.</div>
          </div>
        </div>

        {/* Money-back guarantee */}
        <div className="mt-8 hv-card p-6 sm:p-7 max-w-4xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="money-back">
          <div className="hv-icon-circle-gold w-14 h-14 rounded-full flex items-center justify-center shrink-0">
            <BadgeCheck size={26} strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <div className="hv-heading text-hv-navy text-xl">7-day money-back guarantee</div>
            <p className="mt-1 text-hv-slate text-sm leading-relaxed">
              Try HikmahVerses for a full week. If it isn&apos;t right for your family, cancel in week 1 and we&apos;ll refund your subscription in full — no questions asked.
            </p>
          </div>
          <div className="hidden sm:flex flex-col items-end text-end">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-hv-teal">Applies to</div>
            <div className="hv-heading text-hv-navy text-sm">Every plan · monthly, yearly & family</div>
          </div>
        </div>

        {/* Schools / Madrasas banner */}
        <div className="mt-6 hv-card p-6 sm:p-8 max-w-4xl mx-auto bg-hv-navy text-white flex flex-col md:flex-row items-start md:items-center gap-6" data-testid="schools-banner">
          <div className="hv-icon-circle-gold w-14 h-14 rounded-full flex items-center justify-center shrink-0">
            <School size={26} strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <span className="hv-badge hv-badge-coral">Schools · Institutions · Madrasas</span>
            <h3 className="hv-heading mt-3 text-2xl text-white">Bulk plans built for classrooms.</h3>
            <p className="mt-2 text-white/80 text-sm leading-relaxed max-w-xl">
              Special pricing based on the number of students — with a single teacher dashboard, bulk enrollment, and cohort-wide progress reporting. Perfect for weekend schools, weekday madrasas, and institutions.
            </p>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="schools-tier-hints">
              <span className="hv-badge hv-badge-coral">10–49 students</span>
              <span className="hv-badge hv-badge-coral">50–199 students</span>
              <span className="hv-badge hv-badge-coral">200+ students</span>
            </div>
          </div>
          <a href="mailto:operations@hikmahverses.com?subject=School%20Plan%20Inquiry" data-testid="schools-cta" className="hv-btn-primary inline-flex items-center gap-2 shrink-0">
            Talk to us
          </a>
        </div>
      </section>
      )}

      {/* ASK MY SCHOOL — parent-to-principal pipeline */}
      <AskMySchool />

      {/* FINAL CTA */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <div className="hv-card p-10 sm:p-14 text-center relative overflow-hidden" data-testid="final-cta">
          <span className="hv-badge">Start this week</span>
          <h2 className="hv-heading mt-5 text-3xl sm:text-4xl lg:text-5xl">
            <span className="text-hv-navy">Start with this week&apos;s</span>{" "}
            <span className="text-hv-coral">Qur&apos;anic story</span>
            <span className="text-hv-navy">.</span>
          </h2>
          <p className="mt-4 text-hv-slate max-w-xl mx-auto leading-relaxed">
            Give your child Islamic cartoons that leave behind words, morals, and lessons they can remember.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/signup" data-testid="final-cta-primary" className="hv-btn-primary inline-flex items-center gap-2">
              <Play size={18} fill="white" strokeWidth={0} /> {SHOW_PAYMENT ? "Start Membership · $10/mo" : "Create free account"}
            </Link>
            <WatchSampleDialog testid="final-cta-secondary" />
          </div>
          {SHOW_PAYMENT && <div className="mt-4 text-hv-slate text-xs">$10 / month · cancel anytime</div>}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <span className="hv-badge">FAQ</span>
        <h2 className="hv-heading mt-4 text-3xl sm:text-4xl">
          <span className="text-hv-navy">Quick answers</span>{" "}
          <span className="text-hv-coral">for parents.</span>
        </h2>
        <div className="mt-8">
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {visibleFaqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="hv-card px-6 border-none" data-testid={`faq-${i}`}>
                <AccordionTrigger className="hv-heading text-hv-navy text-start hover:no-underline py-5 text-base sm:text-lg">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-hv-navy/80 font-semibold text-base sm:text-lg leading-relaxed pb-6 pe-4">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-5 sm:px-8 py-8 text-center text-hv-slate text-sm">
        HikmahVerses · 600 stories · 30 Juz · one journey.
      </footer>
    </main>
  );
}
